import React, { useState } from 'react';
import { Plus, Music, FileText, Upload, ChevronDown, ChevronRight } from 'lucide-react';
import DragDropZone from '../DragDropZone';

export default function AddSongForm({ 
  canAddSong, 
  loading, 
  isMobile,
  onAddSong,
  FREE_SONG_LIMIT 
}) {
  const [expanded, setExpanded] = useState(false);
  const [songName, setSongName] = useState('');
  const [songTempo, setSongTempo] = useState('');
  const [songLyrics, setSongLyrics] = useState(null);
  const [songAudio, setSongAudio] = useState(null);

  const handleSubmit = async () => {
    const success = await onAddSong(songName, songTempo, songLyrics, songAudio);
    if (success) {
      setSongName('');
      setSongTempo('');
      setSongLyrics(null);
      setSongAudio(null);
      setExpanded(false);
    }
  };

  return (
    <div className="card-3d rounded-card overflow-hidden mb-4 sm:mb-6">
      <button
        onClick={() => setExpanded(!expanded)}
        disabled={!canAddSong}
        className={`w-full p-4 flex items-center justify-between transition ${
          canAddSong 
            ? 'hover:bg-white/5' 
            : 'opacity-50 cursor-not-allowed'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="bg-brand p-2 rounded-full">
            <Plus className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <p className="font-semibold">Add New Song</p>
            {!canAddSong && (
              <p className="text-xs text-yellow-400">
                Free limit of {FREE_SONG_LIMIT} songs reached
              </p>
            )}
          </div>
        </div>
        {canAddSong && (
          expanded ? <ChevronDown className="w-5 h-5 text-text-subtle" /> : <ChevronRight className="w-5 h-5 text-text-subtle" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6 space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 flex items-center gap-2 bg-transparent border border-surface-border-light rounded-pill px-4 py-3">
              <Music className="w-5 h-5 text-text-subtle" />
              <input
                type="text"
                value={songName}
                onChange={(e) => setSongName(e.target.value)}
                placeholder="Song name"
                className="flex-1 bg-transparent text-white outline-none text-sm"
                disabled={loading}
              />
            </div>
            <div className="sm:w-32 flex items-center gap-2 bg-transparent border border-surface-border-light rounded-pill px-4 py-3">
              <input
                type="number"
                value={songTempo}
                onChange={(e) => setSongTempo(e.target.value)}
                placeholder="BPM"
                min="1"
                max="300"
                className="w-full bg-transparent text-white outline-none text-sm"
                disabled={loading}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-text-subtle mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Lyrics (optional)
              </p>
              {songLyrics ? (
                <div className="bg-transparent border border-surface-border-light rounded-pill px-4 py-3 flex items-center justify-between">
                  <span className="text-sm truncate">{songLyrics.name}</span>
                  <button
                    onClick={() => setSongLyrics(null)}
                    className="text-red-400 hover:text-red-300 text-sm ml-2"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <DragDropZone
                  accept=".doc,.docx,.pdf"
                  onFileSelect={(file) => setSongLyrics(file)}
                  disabled={loading}
                  icon={FileText}
                  message="Drop lyrics (.doc, .pdf)"
                  isMobile={isMobile}
                  compact
                />
              )}
            </div>

            <div>
              <p className="text-sm text-text-subtle mb-2 flex items-center gap-2">
                <Upload className="w-4 h-4" />
                Audio (optional)
              </p>
              {songAudio ? (
                <div className="bg-transparent border border-surface-border-light rounded-pill px-4 py-3 flex items-center justify-between">
                  <span className="text-sm truncate">{songAudio.name}</span>
                  <button
                    onClick={() => setSongAudio(null)}
                    className="text-red-400 hover:text-red-300 text-sm ml-2"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <DragDropZone
                  accept="audio/*,video/mp4,.mp4,.m4a"
                  onFileSelect={(file) => setSongAudio(file)}
                  disabled={loading}
                  icon={Upload}
                  message="Drop audio file"
                  isMobile={isMobile}
                  compact
                />
              )}
            </div>
          </div>

          <button
            onClick={handleSubmit}
            disabled={!songName.trim() || loading}
            className="w-full bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition h-btn font-semibold flex items-center justify-center gap-2 rounded-pill"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="w-5 h-5" />
                Add Song
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
